import { useMemo, useState } from 'react';
import { ChevronsUpDown, Search, X } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface FkFilterPopoverProps {
  /** Map of id -> display label for the related table. */
  lookup: Record<string, string>;
  /** Current filter value as a comma-separated list of selected ids. */
  value: string;
  /** Called with the new comma-separated id list (empty string = no filter). */
  onChange: (value: string) => void;
}

function parseIds(value: string): Set<string> {
  if (!value) {
    return new Set();
  }
  return new Set(value.split(',').filter(Boolean));
}

export function FkFilterPopover({ lookup, value, onChange }: FkFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = useMemo(() => parseIds(value), [value]);

  const entries = useMemo(() => {
    return Object.entries(lookup).sort(([, a], [, b]) => a.localeCompare(b));
  }, [lookup]);

  const filteredEntries = useMemo(() => {
    if (!search) {
      return entries;
    }
    const q = search.toLowerCase();
    return entries.filter(([id, display]) => display.toLowerCase().includes(q) || id === q);
  }, [entries, search]);

  const triggerLabel = (() => {
    if (selected.size === 0) {
      return '';
    }
    if (selected.size === 1) {
      const onlyId = [...selected][0];
      if (!onlyId) {
        return '';
      }
      return lookup[onlyId] ?? onlyId;
    }
    return `${selected.size} selected`;
  })();

  const toggle = (id: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    onChange([...next].join(','));
  };

  const clear = () => {
    onChange('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'flex items-center w-full h-7 rounded-sm border border-muted-foreground/20 bg-transparent dark:bg-input/30 px-2 text-xs text-left',
            selected.size > 0 ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          <span className="truncate flex-1">{triggerLabel}</span>
          {selected.size > 0 ? (
            <span
              role="button"
              tabIndex={0}
              onClick={e => {
                e.stopPropagation();
                clear();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  clear();
                }
              }}
              className="ml-1 rounded-sm p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer"
              aria-label="Clear filter"
            >
              <X className="size-3" />
            </span>
          ) : (
            <ChevronsUpDown className="ml-1 size-3 shrink-0 text-muted-foreground" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <div className="px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 pl-7 text-xs"
              autoFocus
            />
          </div>
        </div>
        <Separator />
        <div className="max-h-[260px] overflow-y-auto px-2 py-2 space-y-0.5">
          {filteredEntries.length === 0 ? (
            <div className="py-2 text-xs text-muted-foreground text-center">No matches</div>
          ) : (
            filteredEntries.map(([id, display]) => {
              const isChecked = selected.has(id);
              return (
                <label
                  key={id}
                  className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                >
                  <Checkbox checked={isChecked} onCheckedChange={checked => toggle(id, !!checked)} />
                  <span className="truncate">{display}</span>
                </label>
              );
            })
          )}
        </div>
        {selected.size > 0 && (
          <>
            <Separator />
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-xs text-muted-foreground">{selected.size} selected</span>
              <button className="text-xs text-muted-foreground hover:text-foreground" onClick={clear}>
                Clear
              </button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
